import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BadgePlus, SquarePen, Trash2, Plus } from 'lucide-react';
import { OrderDTO } from '@/hooks/useOrder';
import { useCreateOrder, useUpdateOrder } from '@/hooks/useOrder';
import { IEmployee, userStatus } from '@/api/employee';
import { ICustomer } from '@/api/customers';
import { createOrderFormSchema, OrderFormValues } from '@/schemas/order';
import LoadingCircle from '@/components/LoadingCircle';
import { useProfileContext } from '@/hooks/useProfileContext';
import TextUserInactive from '@/components/TextUserInactive';
import { toast } from 'react-toastify';
import { Textarea } from '@/components/ui/textarea';
import { OrderStatus } from '@/api/orders';
import DateInput from '@/components/ui/date-input';
import { Card } from '@/components/ui/card';
import { formatCurrency } from '@/lib/utils';
import { useConfirmAmountMismatch } from '@/hooks/useConfirmAmountMismatch';

interface CreateUpdateProps {
  order?: OrderDTO;
  employeesData: IEmployee[];
  customersData: ICustomer[];
}

const CreateUpdate = ({
  order,
  employeesData,
  customersData,
}: CreateUpdateProps) => {
  const { profile } = useProfileContext();
  const isSeller = profile?.role?.key === 'marketing';

  const [isDisabledSubmit, setIsDisabledSubmit] = useState(false);
  const [open, setOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const isEdit = !!order;

  const { mutateAsync: createOrder, isPending } = useCreateOrder();
  const { mutateAsync: updateOrder, isPending: isUpdating } = useUpdateOrder();
  const [total, setTotal] = useState(0);

  const form = useForm<
    OrderFormValues & {
      items: Array<{
        id?: string;
        productName: string;
        price: string;
        quantity: number;
        serialNumber: string;
        description?: string;
      }>;
    }
  >({
    resolver: zodResolver(createOrderFormSchema(isEdit, !!order?.reasonReject)),
    defaultValues: {
      customerId: order?.customer.id.toString() || '',
      employeeId: order?.employee.id.toString() || '',
      items: order?.items || [
        {
          productName: '',
          price: '',
          quantity: 1,
          serialNumber: '',
          description: '',
        },
      ],
      amountToBePaid: order?.amountToBePaid || undefined,
      sellDate: order?.sellDate ? new Date(order.sellDate) : undefined,
      explanation: '',
      budgetMaxBuyGift: order?.budgetMaxBuyGift || undefined,
    },
    mode: 'onChange',
  });

  // Extract save logic to reusable function
  const saveOrder = async (values: OrderFormValues) => {
    const payload = {
      customerId: parseInt(values.customerId),
      employeeId: parseInt(values.employeeId),
      items: values.items,
      amountToBePaid: values?.amountToBePaid || undefined,
      sellDate: (() => {
        if (!values.sellDate) return new Date().toISOString();
        const date = new Date(values.sellDate);
        date.setHours(0, 0, 0, 0);
        return date.toISOString();
      })(),
      explanation: values.explanation,
      budgetMaxBuyGift: values?.budgetMaxBuyGift || undefined,
    };

    if (isEdit) {
      const employee = employeesData.find(
        (employee) => employee.id.toString() === values.employeeId
      );
      if (employee?.status === userStatus.INACTIVE) {
        toast.error(
          'Nhân viên phụ trách không còn hoạt động. Vui lòng thay đổi nhân viên phụ trách cho khách hàng'
        );
        return;
      }
      await updateOrder({
        id: order.id,
        data: payload,
      });
    } else {
      await createOrder(payload);
    }
    setOpen(false);
    form.reset();
  };

  const { checkAndConfirmAmountMismatch, ConfirmDialog } =
    useConfirmAmountMismatch({
      amountToBePaid: form.watch('amountToBePaid') || total,
      totalAmount: total,
      onConfirm: async () => {
        const values = form.getValues();
        await saveOrder(values);
      },
    });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'items',
  });

  const watchItems = fields.map((_field, index) => ({
    price: form.watch(`items.${index}.price`),
    quantity: form.watch(`items.${index}.quantity`),
  }));

  useEffect(() => {
    const total = watchItems.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = item.quantity || 0;
      return sum + price * quantity;
    }, 0);
    setTotal(total);
  }, [watchItems]);

  useEffect(() => {
    if (order) {
      form.reset({
        customerId: order.customer.id.toString(),
        employeeId: order.employee.id.toString(),
        items: order.items.map((item) => ({
          ...item,
          serialNumber: item.serialNumber || '',
          description: item.description || '',
        })),
        amountToBePaid: order.amountToBePaid || undefined,
        sellDate: order.sellDate ? new Date(order.sellDate) : undefined,
        explanation: '',
        budgetMaxBuyGift: order?.budgetMaxBuyGift || undefined,
      });
    }
  }, [order, form]);

  useEffect(() => {
    if (isSeller && open) {
      form.setValue('employeeId', profile?.id?.toString() || '');
    }
  }, [isSeller, form, profile?.id, open]);

  useEffect(() => {
    if (isEdit && order?.reasonReject) {
      form.register('explanation', {
        required: 'Vui lòng nhập lý do muốn được phê duyệt lại',
      });
    }
  }, [isEdit, order?.reasonReject, form]);

  const checkUserReject = (order: OrderDTO) => {
    switch (order.status) {
      case OrderStatus.BUSINESS_REJECTED:
        return 'Quản lý';
      case OrderStatus.ACCOUNTING_REJECTED:
        return 'Kế toán';
      case OrderStatus.ADMIN_REJECTED:
        return 'Admin';
      case OrderStatus.GENERAL_DIRECTOR_REJECTED:
        return 'Giám đốc';
      default:
        return '';
    }
  };

  async function onSubmit(values: OrderFormValues) {
    // Check for duplicate serial numbers before submitting
    const serialNumbers = values.items
      .map((item) => item.serialNumber?.trim())
      .filter(Boolean);
    const uniqueSerialNumbers = new Set(serialNumbers);

    if (uniqueSerialNumbers.size !== serialNumbers.length) {
      // Set error cho từng trường bị trùng
      values.items.forEach((item, idx) => {
        if (
          item.serialNumber &&
          serialNumbers.filter((sn) => sn === item.serialNumber.trim()).length >
            1
        ) {
          form.setError(`items.${idx}.serialNumber`, {
            type: 'manual',
            message: 'Serial Number này đã tồn tại trong đơn hàng',
          });
        }
      });
      return;
    }

    const amountToBePaid = values?.amountToBePaid || total;
    const canProceed = checkAndConfirmAmountMismatch(amountToBePaid, total);
    if (!canProceed) {
      return;
    }

    // If amounts match, save directly
    await saveOrder(values);
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          form.reset();
          setOpen(value);
        }}
      >
        <DialogTrigger asChild>
          <Button
            variant={isEdit ? 'outline' : 'default'}
            className={
              isEdit ? 'luxury-button-outline' : 'luxury-button-primary'
            }
          >
            {isEdit ? (
              <>
                <SquarePen className="w-4 h-4" />
                Sửa
              </>
            ) : (
              <>
                <BadgePlus className="w-4 h-4" />
                Thêm mới
              </>
            )}
          </Button>
        </DialogTrigger>
        <DialogContent className="min-w-[800px] max-h-[95vh] overflow-y-auto luxury-theme">
          <DialogDescription className="sr-only">
            {isEdit ? 'Sửa đơn hàng' : 'Thêm đơn hàng'}
          </DialogDescription>
          <DialogHeader className="luxury-header">
            <DialogTitle className="luxury-title">
              {isEdit ? 'Cập nhật đơn hàng' : 'Tạo đơn hàng mới'}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-8"
              autoComplete="off"
            >
              <div className="luxury-card">
                <h3 className="luxury-subtitle mb-4">Thông tin đơn hàng</h3>
                <div className="flex flex-col gap-6 items-start">
                  <div className="flex gap-6 w-full items-start">
                    <FormField
                      control={form.control}
                      name="customerId"
                      render={({ field }) => (
                        <FormItem className="w-1/2">
                          <Label required className="luxury-label">
                            Khách hàng
                          </Label>
                          <FormControl>
                            <Select
                              value={field.value.toString()}
                              onValueChange={(value) => {
                                field.onChange(value);
                                const customer = customersData.find(
                                  (customer) =>
                                    parseInt(value) === parseInt(customer.id)
                                );
                                if (customer) {
                                  form.setValue(
                                    'employeeId',
                                    customer.seller.id.toString()
                                  );
                                  if (
                                    customer.seller.status ===
                                    userStatus.INACTIVE
                                  ) {
                                    toast.error(
                                      'Nhân viên phụ trách không còn hoạt động. Vui lòng thay đổi nhân viên phụ trách cho khách hàng'
                                    );
                                    setIsDisabledSubmit(true);
                                  } else {
                                    setIsDisabledSubmit(false);
                                  }
                                }
                              }}
                            >
                              <SelectTrigger className="w-full luxury-input">
                                <SelectValue placeholder="Chọn khách hàng" />
                              </SelectTrigger>
                              <SelectContent className="border-luxury-200">
                                {customersData.map((customer) => (
                                  <SelectItem
                                    key={customer.id}
                                    value={customer.id.toString()}
                                  >
                                    KH-{customer.id} - {customer.fullName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="employeeId"
                      render={({ field }) => (
                        <FormItem className="w-1/2">
                          <Label required className="luxury-label">
                            Nhân viên phụ trách
                          </Label>
                          <FormControl>
                            <Select
                              value={field.value.toString()}
                              onValueChange={field.onChange}
                              disabled={true}
                            >
                              <SelectTrigger className="w-full luxury-input">
                                <SelectValue placeholder="Chọn nhân viên" />
                              </SelectTrigger>
                              <SelectContent className="border-luxury-200">
                                {isSeller ? (
                                  <SelectItem
                                    value={profile?.id?.toString() || ''}
                                  >
                                    {profile?.name}
                                  </SelectItem>
                                ) : (
                                  employeesData.map((employee) => (
                                    <SelectItem
                                      key={employee.id}
                                      value={employee.id.toString()}
                                      disabled={
                                        employee.status === userStatus.INACTIVE
                                      }
                                    >
                                      {employee.name}
                                      <TextUserInactive
                                        status={employee.status}
                                      />
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="w-full space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="luxury-label">
                        Danh sách sản phẩm - Tổng tiền:{' '}
                        <span className="font-bold text-luxury-800">
                          {formatCurrency(total)}
                        </span>
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="luxury-button-outline"
                        onClick={() =>
                          append({
                            productName: '',
                            price: '',
                            quantity: 1,
                            serialNumber: '',
                            description: '',
                            ...(isEdit ? { id: '' } : {}),
                          })
                        }
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Thêm sản phẩm
                      </Button>
                    </div>

                    {fields.map((field, index) => (
                      <Card key={field.id} className="p-4 border-luxury-100">
                        <div className="flex justify-between items-start mb-4">
                          <h4 className="font-medium text-luxury-800">
                            Sản phẩm {index + 1}
                          </h4>
                          {fields.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => remove(index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        {isEdit && order?.items.length > 0 && (
                          <FormField
                            control={form.control}
                            name={`items.${index}.id`}
                            render={({ field }) => (
                              <FormItem className="hidden">
                                <Label className="luxury-label">ID</Label>
                                <FormControl>
                                  <Input {...field} className="luxury-input" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                        <div className="grid grid-cols-2 gap-4 items-start">
                          <FormField
                            control={form.control}
                            name={`items.${index}.productName`}
                            render={({ field }) => (
                              <FormItem>
                                <Label required className="luxury-label">
                                  Tên sản phẩm
                                </Label>
                                <FormControl>
                                  <Input {...field} className="luxury-input" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`items.${index}.price`}
                            render={({ field }) => (
                              <FormItem>
                                <Label required className="luxury-label">
                                  Giá (VNĐ)
                                </Label>
                                <FormControl>
                                  <Input
                                    {...field}
                                    type="number"
                                    className="luxury-input"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`items.${index}.quantity`}
                            render={({ field }) => (
                              <FormItem>
                                <Label required className="luxury-label">
                                  Số lượng
                                </Label>
                                <FormControl>
                                  <Input
                                    {...field}
                                    type="number"
                                    min="1"
                                    className="luxury-input"
                                    onChange={(e) =>
                                      field.onChange(parseInt(e.target.value))
                                    }
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`items.${index}.serialNumber`}
                            render={({ field }) => (
                              <FormItem>
                                <Label required className="luxury-label">
                                  Serial Number
                                </Label>
                                <FormControl>
                                  <Input
                                    {...field}
                                    className="luxury-input"
                                    placeholder="VD: SN-123456"
                                    onChange={(e) => {
                                      field.onChange(e);
                                      const value = e.target.value.trim();
                                      const items = form.getValues('items');
                                      const duplicate = items.filter(
                                        (item, idx2) =>
                                          idx2 !== index &&
                                          item.serialNumber?.trim() === value
                                      );
                                      if (value && duplicate.length > 0) {
                                        form.setError(
                                          `items.${index}.serialNumber`,
                                          {
                                            type: 'manual',
                                            message:
                                              'Serial Number này đã tồn tại trong đơn hàng',
                                          }
                                        );
                                      } else {
                                        form.clearErrors(
                                          `items.${index}.serialNumber`
                                        );
                                      }
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-4 items-start">
                          <FormField
                            control={form.control}
                            name={`items.${index}.description`}
                            render={({ field }) => (
                              <FormItem>
                                <Label className="luxury-label">Mô tả</Label>
                                <FormControl>
                                  <Textarea
                                    {...field}
                                    className="luxury-input min-h-[80px]"
                                    placeholder="Mô tả sản phẩm (tùy chọn)"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </Card>
                    ))}
                  </div>

                  <div className="flex gap-6 w-full items-start">
                    <FormField
                      control={form.control}
                      name="sellDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col w-1/2">
                          <DateInput
                            datePickerOpen={datePickerOpen}
                            setDatePickerOpen={setDatePickerOpen}
                            field={field}
                            label="Ngày bán"
                            placeholder="Chọn ngày bán"
                            required
                            disabledTime={(date: Date) =>
                              date > new Date() || date < new Date('1900-01-01')
                            }
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="budgetMaxBuyGift"
                      render={({ field }) => (
                        <FormItem className="w-1/2">
                          <Label className="luxury-label">
                            Số tiền tối đa mua quà
                          </Label>
                          <FormControl>
                            <Input
                              value={field.value || ''}
                              type="number"
                              className="luxury-input"
                              min="1"
                              onChange={(e) =>
                                field.onChange(parseInt(e.target.value))
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="amountToBePaid"
                      render={({ field }) => (
                        <FormItem className="w-1/2">
                          <Label className="luxury-label">
                            Số tiền phải trả
                          </Label>
                          <FormControl>
                            <Input
                              value={
                                parseFloat(`${field.value}`) ||
                                parseFloat(total.toString())
                              }
                              type="number"
                              className="luxury-input"
                              min="1"
                              onChange={(e) =>
                                field.onChange(parseInt(e.target.value))
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {isEdit && order.reasonReject !== '' && (
                    <div className="flex w-full items-start flex-col">
                      <div className="p-4 rounded-md w-full mb-4 luxury-card !shadow-none">
                        <Label className="luxury-label">
                          Đơn hàng của bạn bị từ chối bởi{' '}
                          <span className="font-bold">
                            {checkUserReject(order)}
                          </span>{' '}
                          với lý do:
                        </Label>
                        <div className="text-base font-medium  whitespace-pre-line break-words w-full mt-1">
                          {order?.reasonReject}
                        </div>
                      </div>
                      <FormField
                        control={form.control}
                        name="explanation"
                        render={({ field }) => (
                          <FormItem className="w-full">
                            <Label required className="luxury-label">
                              Lý do muốn được phê duyệt lại
                            </Label>
                            <FormControl>
                              <Textarea
                                {...field}
                                className="min-h-[100px] luxury-input bg-white"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full mt-6 luxury-button-primary"
                disabled={isPending || isUpdating || isDisabledSubmit}
              >
                {isPending || isUpdating ? (
                  <LoadingCircle />
                ) : (
                  <>{isEdit ? 'Cập nhật đơn hàng' : 'Tạo đơn hàng mới'}</>
                )}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </>
  );
};

export default CreateUpdate;
